import { ArrowRight, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import DynIcon from "@/components/DynIcon";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import { CONSULTING_SERVICES, IMAGES } from "@/lib/site";

const PROCESS = [
  { n: "01", t: "Discovery Call", d: "We learn your property layout, devices and pain points — dead zones, buffering, video-call drops." },
  { n: "02", t: "On-Site Survey", d: "We map your signal with real RF measurements and predictive heat maps to find exactly what's wrong." },
  { n: "03", t: "Design & Install", d: "We design the right access points, mesh nodes and channels — then set it all up and validate coverage." },
  { n: "04", t: "Optimize & Support", d: "We fine-tune firmware and channels, and stay on call so your network keeps performing." },
];

const OUTCOMES = [
  "Whole-property coverage — no more dead zones",
  "Rock-solid video calls & online gaming",
  "Seamless roaming from room to room",
  "Right-sized gear, no overspending",
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "WiFi Network Design & Consulting",
  provider: { "@type": "Organization", name: "CYAN Wireless", telephone: "+1-970-440-3178" },
  areaServed: "Cortez, Dolores, Yellow Jacket & Lewis, Colorado",
  description: "Professional home and business WiFi consulting: network design, coverage and heat-map surveys, mesh setup and troubleshooting.",
};

export default function Consulting() {
  return (
    <>
      <Seo
        title="WiFi Consulting — Home & Business Network Design & Optimization | CYAN Wireless"
        description="Enterprise-grade WiFi for your home or business. CYAN Wireless offers network design, coverage & heat-map surveys, mesh setup and expert troubleshooting in Cortez, Dolores & Yellow Jacket, CO. Request a consultation."
        path="/wifi-consulting"
        jsonLd={jsonLd}
      />
      <PageHero
        overline="New · WiFi Consulting"
        titleLines={["Enterprise WiFi", <span key="2">for home & <span className="text-cyan-400 text-glow">business.</span></span>]}
        subtitle="Great internet is only half the story — the WiFi inside your walls matters just as much. Our experts design, survey, install and optimize wireless networks so every corner of your property runs fast and reliable."
        image={IMAGES.routerNeon}
        imageAlt="Modern wireless router with neon lighting"
      >
        <a href="#consult" className="inline-flex items-center gap-2 bg-cyan-500 text-ink-950 font-display font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-full btn-glow hover:bg-cyan-400" data-testid="consult-hero-cta">
          Request a Consultation <ArrowRight size={16} />
        </a>
      </PageHero>

      {/* Services bento */}
      <section className="py-24 sm:py-32 bg-ink-950">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="overline mb-4">What we do</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter text-white leading-[0.95]">Services built around coverage.</h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 gap-5">
            {CONSULTING_SERVICES.map((s) => (
              <motion.div key={s.title} variants={staggerItem} className="group relative bg-ink-900 border border-white/5 rounded-2xl p-8 overflow-hidden hover:border-cyan-500/50 transition-colors">
                <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-cyan-500/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative flex items-start gap-5">
                  <div className="w-12 h-12 shrink-0 grid place-items-center rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 group-hover:scale-110 transition-transform">
                    <DynIcon name={s.icon} size={22} />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-xl text-white mb-2">{s.title}</h3>
                    <p className="text-zinc-400 leading-relaxed text-[15px]">{s.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 sm:py-28 bg-ink-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="mb-14">
            <p className="overline mb-4">The process</p>
            <h2 className="font-display font-extrabold text-4xl tracking-tighter text-white">Four steps to flawless WiFi.</h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.map((p) => (
              <motion.div key={p.n} variants={staggerItem} className="bg-ink-950 border border-white/5 rounded-2xl p-7 hover:border-cyan-500/40 transition-colors">
                <div className="font-mono-tech text-cyan-400 text-lg mb-4">{p.n}</div>
                <h3 className="font-display font-bold text-lg text-white mb-2">{p.t}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{p.d}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Outcomes + form */}
      <section id="consult" className="py-24 sm:py-32 bg-ink-950 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <p className="overline mb-4">What you get</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter text-white leading-[0.95] mb-8">
              WiFi that just<br />works. Everywhere.
            </h2>
            <ul className="space-y-4">
              {OUTCOMES.map((o) => (
                <li key={o} className="flex items-center gap-3 text-white text-lg">
                  <CheckCircle2 className="text-cyan-400 shrink-0" size={20} /> {o}
                </li>
              ))}
            </ul>
            <div className="mt-10 rounded-2xl overflow-hidden border border-white/10">
              <img src={IMAGES.routerMesh} alt="Mesh WiFi system" className="w-full h-56 object-cover" />
            </div>
          </Reveal>

          <Reveal delay={0.1} className="bg-ink-900 border border-white/10 rounded-2xl p-8">
            <h3 className="font-display font-bold text-2xl text-white mb-2">Request a consultation</h3>
            <p className="text-zinc-400 text-sm mb-6">Tell us about your space and we'll reach out to schedule your survey.</p>
            <LeadForm type="consultation" submitLabel="Request Consultation" showAddress showConsulting />
          </Reveal>
        </div>
      </section>
    </>
  );
}
